import { z } from 'zod';
import {
  DeferredItemSchema,
  FINISH_NOTES_MAX_CHARS,
  SlugInputSchema,
  TOOL_JUSTIFICATION_MAX_CHARS,
  clippedText,
} from './entities.js';

/**
 * Case Wiki — payloads des tools du Linter agent (PR3 / TICKET-124).
 *
 * Le Linter tourne APRÈS l'ingest et AVANT le Reviewer avec
 * pour mission de nettoyer le wiki : doublons potentiels (slugs proches,
 * titres similaires), entités orphelines, slugs malformés, etc.
 *
 * Pipeline : (1) SQL scan détecte les anomalies, (2) LLM propose et applique
 * les corrections via 3 tools : `merge_facts`, `merge_parties`, `rename_slug`.
 *
 * Spec : `docs/architecture/case-wiki/data-model.md` §5.
 */

// ---------------------------------------------------------------------------
// merge_facts
// ---------------------------------------------------------------------------

/**
 * Fusionne deux faits doublons : `loser_slug` est supprimé, ses liens
 * (parties, pièces, contradictions) sont migrés vers `winner_slug`.
 */
export const MergeFactsPayloadSchema = z
  .object({
    winner_slug: SlugInputSchema,
    loser_slug: SlugInputSchema,
    /**
     * Justification (50 caractères au moins) : elle oblige l'agent à dire pourquoi c'est un
     * doublon. Elle n'est pas transmise au repository — coupée au-delà du maximum, jamais refusée.
     */
    rationale: clippedText(50, TOOL_JUSTIFICATION_MAX_CHARS),
  })
  .superRefine((value, ctx) => {
    if (value.winner_slug === value.loser_slug) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['loser_slug'],
        message: 'winner_slug and loser_slug must differ',
      });
    }
  });
export type MergeFactsPayload = z.infer<typeof MergeFactsPayloadSchema>;

// ---------------------------------------------------------------------------
// merge_parties
// ---------------------------------------------------------------------------

/** Même contrat que merge_facts mais pour les parties. */
export const MergePartiesPayloadSchema = z
  .object({
    winner_slug: SlugInputSchema,
    loser_slug: SlugInputSchema,
    rationale: clippedText(50, TOOL_JUSTIFICATION_MAX_CHARS),
  })
  .superRefine((value, ctx) => {
    if (value.winner_slug === value.loser_slug) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['loser_slug'],
        message: 'winner_slug and loser_slug must differ',
      });
    }
  });
export type MergePartiesPayload = z.infer<typeof MergePartiesPayloadSchema>;

// ---------------------------------------------------------------------------
// rename_slug
// ---------------------------------------------------------------------------

/**
 * Renomme un slug (fait, partie ou pièce). Utilisé quand l'ingest a posé un
 * slug sub-optimal (typo, manque de contexte).
 *
 * `entity_type` discrimine la table cible. `old_slug` doit exister, `new_slug`
 * ne doit pas. La méthode repo migre toutes les FK qui référencent l'ancien.
 * (`article` / `jurisprudence` retirés le 2026-07-03 avec le sous-graphe
 * références juridiques.)
 *
 * Note : pour `entity_type = 'piece'`, la colonne cible est
 * `case_documents.wiki_slug` (pas `slug`).
 */
export const RenameSlugPayloadSchema = z
  .object({
    entity_type: z.enum(['fact', 'party', 'piece']),
    old_slug: SlugInputSchema,
    new_slug: SlugInputSchema,
    rationale: clippedText(20, TOOL_JUSTIFICATION_MAX_CHARS),
  })
  .superRefine((value, ctx) => {
    if (value.old_slug === value.new_slug) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['new_slug'],
        message: 'old_slug and new_slug must differ',
      });
    }
  });
export type RenameSlugPayload = z.infer<typeof RenameSlugPayloadSchema>;

// ---------------------------------------------------------------------------
// finish_lint
// ---------------------------------------------------------------------------

/**
 * Payload terminal du Linter. Persisté dans `case_wiki_runs.payload.finish`
 * (run_type='lint').
 */
export const FinishLintPayloadSchema = z.object({
  summary: z.string().min(20).max(2000),
  facts_merged: z.number().int().nonnegative().default(0),
  parties_merged: z.number().int().nonnegative().default(0),
  slugs_renamed: z.number().int().nonnegative().default(0),
  /**
   * Liens party↔party créés via `link_party_to_party` durant la pass lint
   * (inférences cross-pièces sur paires co-mentionnées).
   */
  party_links_created: z.number().int().nonnegative().default(0),
  /** Anomalies détectées par le SQL scan mais non corrigées (raison expliquée). */
  anomalies_skipped: z.number().int().nonnegative().default(0),
  /** Notes pour le Reviewer ou pour l'humain. */
  notes: clippedText(0, FINISH_NOTES_MAX_CHARS).optional(),
  /** Items différés par l'agent lint — carry-forward vers la phase review. */
  deferred_items: z.array(DeferredItemSchema).max(200).default([]),
});
export type FinishLintPayload = z.infer<typeof FinishLintPayloadSchema>;

// ---------------------------------------------------------------------------
// SQL scan results (input du LLM)
// ---------------------------------------------------------------------------

/**
 * Résultat d'une règle de scan SQL. Chaque rule détecte un type d'anomalie
 * et retourne 0..N items à examiner par le LLM. Les items sont sérialisés
 * dans le user prompt du Linter pour l'inviter à proposer des corrections.
 *
 * Catalogue des règles (M068, MVP) :
 *  - `duplicate_facts_by_title`        : faits avec title très proche (Levenshtein)
 *  - `duplicate_parties_by_title`      : parties avec title très proche
 *  - `orphan_facts`                    : facts sans aucune partie liée
 *  - `orphan_parties`                  : parties sans aucun fait
 *  - `facts_without_event_date`        : faits sans event_date (à corriger ou marquer)
 *  - `slugs_with_typo_pattern`         : slugs contenant des patterns suspects
 *  - `parties_with_inconsistent_side`  : parties dont `side` diverge des faits liés
 *  - `unlinked_party_pairs_co_mentioned`: paires de parties qui co-apparaissent
 *      fréquemment dans les faits sans aucun `case_party_links` actif
 *      (candidates à `link_party_to_party` post-ingest).
 */
export const LintAnomalyKindSchema = z.enum([
  'duplicate_facts_by_title',
  'duplicate_parties_by_title',
  'orphan_facts',
  'orphan_parties',
  'facts_without_event_date',
  'slugs_with_typo_pattern',
  'parties_with_inconsistent_side',
  'unlinked_party_pairs_co_mentioned',
]);
export type LintAnomalyKind = z.infer<typeof LintAnomalyKindSchema>;
